import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Modal as BaseModal } from 'component-library-and-tools';

import { closeModal } from 'services/modal/actions';
import { isModalOpen } from 'services/modal/getters';
import { colors } from 'lib/prisma-styles';

import { ModalProps } from './types';
import { RootState } from '../../..';

const MODAL_OVERLAY_CLASS_NAME = 'modal__overlay';
const MODAL_CONTAINER_CLASS_NAME = 'modal__container';
const MODAL_TITLE_CLASS_NAME = 'modal__title';

const StyledModal = styled(BaseModal)<Pick<ModalProps, 'isFullscreenMode'>>`
  .${MODAL_CONTAINER_CLASS_NAME} {
    background-color: ${colors.background.page};
  }

  ${({ isFullscreenMode }) => isFullscreenMode && css`
    width: 100%;
    height: 100%;

    & {
      .${MODAL_OVERLAY_CLASS_NAME} {
        padding-right: 0;
        padding-bottom: 0;
        padding-left: 0;
      }

      .${MODAL_CONTAINER_CLASS_NAME} {
        width: 100%;
        height: 100%;
        padding: 0 13.167rem 1.3rem;
      }

      .${MODAL_TITLE_CLASS_NAME} {
        margin: 1.5rem 0;
      }
    }
  `}
`;

const Modal = ({
  children,
  name,
  isOpen = false,
  onClose = () => {},
  isFullscreenMode = true,
  overlayClassName,
  containerClassName,
  titleClassName,
  ...otherProps
}: ModalProps) => {
  const dispatch = useDispatch();
  const openStatus = isOpen || useSelector<RootState, boolean>((state) => isModalOpen(state, name));

  const handleModalClose = useCallback(() => {
    onClose();
    dispatch(closeModal(name));
  }, [name, onClose]);

  return (
    <StyledModal
      isFullscreenMode={isFullscreenMode}
      overlayClassName={classnames(MODAL_OVERLAY_CLASS_NAME, overlayClassName)}
      containerClassName={classnames(MODAL_CONTAINER_CLASS_NAME, containerClassName)}
      titleClassName={classnames(MODAL_TITLE_CLASS_NAME, titleClassName)}
      isOpen={openStatus}
      onClose={handleModalClose}
      {...otherProps}
    >
      {children}
    </StyledModal>
  );
};

export default Modal;
